import React, { useCallback } from 'react';
import { styled } from '@compiled/react';
import { Box, xcss } from '@atlaskit/primitives';
import SectionMessage from '@atlaskit/section-message';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { ff } from '@atlassian/jira-feature-flagging';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl, FormattedMessage } from '@atlassian/jira-intl';
import { LEARN_MORE_LINK } from '../../../common/constants';
import type { ValidationErrorType } from '../../../common/constants/errors';
import { ISSUEID_FIELDTYPE } from '../../../common/constants/fields';
import type { ColumnName } from '../../../common/types';
import {
	hasMissingFields,
	getColumnErrors,
	useGetValidationErrorMessage,
} from '../../../common/utils';
import { useMappingState } from '../../../controllers/import-state';
import messages from './messages';
import { boldElement, getMissingFieldName, useValidationErrors } from './utils';

type Props = {
	showLearnMoreLink?: boolean;
};

export const ValidationError = ({ showLearnMoreLink }: Props) => {
	const { formatMessage } = useIntl();
	const [mappingState] = useMappingState();
	const { getValidationErrorMessage } = useGetValidationErrorMessage();
	const { issueLinkingColumnErrors, otherColumnErrors } = getColumnErrors(mappingState);
	const { columnErrors, missingErrors, count } = useValidationErrors(mappingState);

	const getColumnError = useCallback(
		([name, errorType]: [ColumnName, ValidationErrorType]) => (
			<ListItem key={name}>
				<LightBold>{mappingState?.columns[name].field?.fieldName}:</LightBold>{' '}
				{getValidationErrorMessage(errorType, true)}
			</ListItem>
		),
		[getValidationErrorMessage, mappingState?.columns],
	);

	const getOtherColumnErrors = useCallback(() => {
		if (Object.keys(otherColumnErrors).length === 0) return null;

		return (
			<li>
				<FormattedMessage
					values={{
						heading: <Bold>{formatMessage(messages.columnErrorsHeading)}</Bold>,
					}}
					{...messages.columnErrors}
				/>
				<List>{Object.entries(otherColumnErrors).map(getColumnError)}</List>
			</li>
		);
	}, [formatMessage, getColumnError, otherColumnErrors]);

	const getIssueLinkingColumnError = useCallback(
		() =>
			Object.keys(issueLinkingColumnErrors).map((name: ColumnName) => (
				<li key={name}>
					<FormattedMessage
						values={{
							heading: (
								<Bold>
									{formatMessage(messages.issueLinkingColumnErrorHeading, {
										name,
									})}
								</Bold>
							),
						}}
						{...messages.issueLinkingColumnError}
					/>
				</li>
			)),
		[formatMessage, issueLinkingColumnErrors],
	);

	const getMissingFieldErrors = useCallback(() => {
		if (!hasMissingFields(mappingState)) return null;
		const isIssueLinksSelected = ff('x2c-issuelinking-frontend_tuyzk')
			? !!mappingState &&
				mappingState.availableIssueLinkFields.some(
					(issueLinkField) =>
						!!issueLinkField.fieldId && mappingState.mappedJiraFieldIds.has(issueLinkField.fieldId),
				)
			: false;
		return mappingState?.validationError?.missingFields.map((fieldId) => {
			const missingField = getMissingFieldName(fieldId, mappingState);
			return (
				<li key={fieldId}>
					<FormattedMessage
						values={{
							heading: (
								<Bold>
									{fieldId === ISSUEID_FIELDTYPE && isIssueLinksSelected
										? formatMessage(messages.missingIssueIdHeading)
										: formatMessage(messages.missingFieldHeading, {
												missingField,
											})}
								</Bold>
							),
							missingField,
							isIssueId: fieldId === 'issueId',
							isIssueLinksSelected,
						}}
						{...messages.missingField}
					/>
				</li>
			);
		});
	}, [formatMessage, mappingState]);

	if (fg('enable-x2c-standardised-flow')) {
		return (
			<Box xcss={sectionWrapperStyles}>
				<SectionMessage
					testId="project-create-importer-upload-flow.ui.mapping-screen.validation-error.section-message"
					appearance="error"
					title={formatMessage(messages.titleStandard, { count })}
				>
					<Box xcss={contentStyles}>
						<Box as="ol" xcss={listStyle}>
							{missingErrors.map((message, index) => (
								<Box as="li" key={index}>
									{message}
								</Box>
							))}
							{columnErrors.length > 0 && (
								<Box as="li">
									{formatMessage(messages.formattingErrorHeading, { b: boldElement })}
									<Box as="ol" xcss={sublistStyle}>
										{columnErrors.map((column) => (
											<Box as="li" key={column}>
												<Box as="b">{column}</Box>
											</Box>
										))}
									</Box>
								</Box>
							)}
						</Box>
					</Box>
				</SectionMessage>
			</Box>
		);
	}

	return (
		<Box xcss={sectionWrapperStyles}>
			<SectionMessage
				testId="project-create-importer-upload-flow.ui.mapping-screen.validation-error.section-message"
				appearance="error"
				title={formatMessage(messages.validationErrorTitle)}
			>
				<ContentWrapper>
					<OrderedList data-testid="project-create-importer-upload-flow.ui.mapping-screen.validation-error.list">
						{getMissingFieldErrors()}
						{getOtherColumnErrors()}
						{getIssueLinkingColumnError()}
					</OrderedList>
					{showLearnMoreLink && (
						<LearnMore href={LEARN_MORE_LINK} target="_blank">
							{formatMessage(messages.learnMoreSpork)}
						</LearnMore>
					)}
				</ContentWrapper>
			</SectionMessage>
		</Box>
	);
};

const contentStyles = xcss({
	maxHeight: '10vh',
	overflowY: 'auto',
});

const listStyle = xcss({
	paddingLeft: 'space.200',
});

const sublistStyle = xcss({
	paddingLeft: 'space.200',
	listStyleType: 'lower-alpha',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const OrderedList = styled.ol({
	paddingLeft: token('space.200', '16px'),
	marginBottom: token('space.200', '16px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const List = styled.ul({
	padding: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ListItem = styled.li({
	listStyle: 'inside',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Bold = styled.span({
	fontWeight: 'bold',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LightBold = styled.span({
	fontWeight: 500,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LearnMore = styled.a({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.accent.blue', colors.B400),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentWrapper = styled.div({
	maxHeight: '10vh',
	overflowY: 'auto',
});

const sectionWrapperStyles = xcss({
	marginBottom: 'space.300',
});
